import { AREA_SWITCHES, PERMISSION_CATALOG, type PermissionArea, type PermissionAreaInfo } from '@lib/constants/permissions'
import { grantsSchema, PERMISSION_LEVELS, type Grants, type PermissionLevel } from '@lib/dto/tenant-role.dto'
import type { TenantFeature } from '@lib/enums/tenant-feature.enum'
import type { TenantRoleReach } from '@lib/enums/tenant-role-reach.enum'

export { PERMISSION_LEVELS, type PermissionLevel }

export const LEVEL_LABEL: Record<PermissionLevel, string> = { none: 'None', view: 'View', edit: 'Edit' }

export const REACH_LABEL: Record<TenantRoleReach, string> = { venues: 'Assigned venues', company: 'Every venue' }

export const REACH_HINT: Record<TenantRoleReach, string> = {
  venues: 'Each person works at the venues they’re assigned.',
  company: 'Every venue the company has, including ones added later.',
}

/**
 * The areas worth showing to this company: one that needs a feature the
 * company doesn't have is left out, as the spec says, but its grant is kept as
 * it was, so turning the feature on later finds it where it was left.
 */
export function areasFor(features: readonly TenantFeature[]): PermissionAreaInfo[] {
  return PERMISSION_CATALOG.filter((area) => area.features.length === 0 || area.features.some((f) => features.includes(f)))
}

/**
 * The grants with one area at a new level. Lowering an area from Edit also
 * turns off its switches (deleting events, the price list, refunds), which only
 * go with Edit. A level the area doesn't have leaves the grants as they were.
 */
export function withLevel(grants: Grants, key: PermissionArea, level: PermissionLevel): Grants {
  const switchesOff = level === 'edit' ? {} : Object.fromEntries((AREA_SWITCHES[key] ?? []).map((s) => [s, false]))
  const next = grantsSchema.safeParse({ ...grants, [key]: level, ...switchesOff })
  return next.success ? next.data : grants
}

/** What a role can do in a line: "Edit: Events, Clients · View: Ads". */
export function grantsSummary(grants: Grants, areas: readonly PermissionAreaInfo[]): string {
  const at = (level: PermissionLevel) => areas.filter((area) => grants[area.key] === level).map((area) => area.label)
  const parts = [
    ...(at('edit').length > 0 ? [`Edit: ${at('edit').join(', ')}`] : []),
    ...(at('view').length > 0 ? [`View: ${at('view').join(', ')}`] : []),
  ]
  return parts.length === 0 ? 'Nothing yet' : parts.join(' · ')
}

export function peopleLine(people: number): string {
  if (people === 0) return 'Nobody holds this role yet.'
  return people === 1 ? '1 person holds this role.' : `${String(people)} people hold this role.`
}
